import * as React from 'react'
import styled from 'styled-components'
import { FormattedMessage } from 'react-intl'
import H5 from 'web-makeup/dist/H5'
import DatePicker from 'web-makeup/dist/DatePicker'
import Radio from 'web-makeup/dist/Radio'
import CloudinaryImage from 'web-makeup/dist/CloudinaryImage'
import * as moment from 'moment'
import Alert from 'web-makeup/dist/Alert'

import { GetQuoteForm } from '../../typings/types'

import { BoxHeader, H5SubText } from './../Purchase'
import SelectedIcon from './../SelectedIcon'

const RadioButton = Radio.Button
const RadioGroup = Radio.Group

export const ISO_8601_WITHOUT_FRACTIONAL_SECONDS = 'YYYY-MM-DDTHH:mm:ss[Z]'
const DISPLAY_DATE_FORMAT = 'D MMM YYYY'

export const StyledDatePicker = styled(DatePicker as any)`
  grid-column: span 3;

  &&& {
    margin-top: 1rem;
    width: 100%;
  }

  @media (min-width: 48rem) {
    grid-column: 0;

    &&& {
      margin-top: 0;
    }
  }
`

const Option: any = styled(RadioButton)`
  &&& {
    &:first-child {
      flex-basis: 45%;
      margin-right: 0.5rem;
    }
    border-radius: 6px;
    background-color: white;
    border: solid 1px #bebebe;

    padding: 1rem;

    width: 100%;
    height: auto;

    color: currentColor;

    .ant-radio-button {
      display: none;
    }

    filter: grayscale(100%);

    &&&.ant-radio-button-wrapper-checked {
      border-style: solid;
      border-width: 1px;
      border-color: ${(p: { color?: string | undefined }) => p.color || '#7ed321'};

      /* Ant design has !important on .ant-radio-button-wrapper-checked:first-child */

      /* So in order to overwrite it we must have important here */

      /* stylelint-disable-next-line declaration-no-important */
      box-shadow: 0 6px 10px 0 rgba(0, 0, 0, 0.2) !important;
      filter: none;
    }

    &.ant-radio-button-wrapper-disabled {
      background: whitesmoke;
    }
  }
`

const OptionType = styled.span`
  font-weight: 900;
  padding-left: 1rem;
  font-size: 1rem;
  margin-right: 0.25rem;

  @media (min-width: 48rem) {
    margin-right: 0;
  }
`

const OptionImage = styled(CloudinaryImage)`
  font-weight: 500;
`

const OptionDesc = styled.span`
  color: #757575;
  font-size: 1rem;
`

export const TimeOption = styled(Option)`
  /* Actual content of the button */
  .ant-radio-button + span {
    display: block;

    /* stylelint-disable-next-line selector-max-compound-selectors */
    > i {
      display: none;

      @media (min-width: 48rem) {
        display: block;
      }
    }

    @media (min-width: 48rem) {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;

      @supports (display: grid) {
        display: grid;
        grid-template-columns: 2rem 1fr 1fr;
      }
    }
  }

  ${OptionDesc} {
    text-align: right;
  }

  @media (min-width: 48rem) {
    &:nth-of-type(2) {
      grid-column: 2 / span 2;
    }

    ${OptionType} {
      padding-left: 0;
    }

    .ant-radio-button + span {
      grid-template-columns: 1fr;
      justify-items: center;
    }

    /* stylelint-disable selector-max-compound-selectors */

    /* Content of "Schedule" radio */
    &:nth-of-type(2) .ant-radio-button + span {
      grid-template-columns: repeat(12, 1fr);
      grid-template-rows: repeat(3, min-content);

      ${OptionImage} {
        grid-column: 2 / span 3;
        grid-row: 1;
      }

      ${OptionType} {
        grid-column: 2 / span 3;
        grid-row: 2;
      }

      ${OptionDesc} {
        grid-column: 2 / span 3;
        grid-row: 3;
      }

      ${StyledDatePicker} {
        grid-column: 6 / span 6;
        grid-row: 2;
      }
    }
    /* stylelint-enable */
  }
`

const Options = styled(RadioGroup as any)`
  &&& {
    ${TimeOption} + ${TimeOption} {
      margin-top: 1rem;
    }

    @media (min-width: 48rem) {
      /* IE11 */
      display: flex;

      /* end IE11 */

      @supports (display: grid) {
        display: grid;
        grid-gap: 1rem;
        grid-template-columns: repeat(3, 1fr);
      }

      ${TimeOption} + ${TimeOption} {
        margin-top: 0;
      }
    }
  }
`

const StyledAlert = styled(Alert)`
  && {
    margin-top: 0.5rem;
    margin-bottom: 1.5rem;

    .ant-alert-message {
      display: none;
    }
  }
`

const IMAGE_SIZES = {
  mobile: {
    height: 32,
    width: 32,
  },
  tablet: {
    height: 56,
    width: 56,
  },
}

export const mapSelectedToOption = selected => (selected === null ? 'now' : 'schedule')

export const mapOptionToSelected = (option, scheduledDate) =>
  option === 'now' ? null : scheduledDate

export const getPicker = ref => (ref.current && ref.current.picker ? ref.current.picker : undefined)

const update = (onChange?: OwnProps['onChange']) => (date: moment.Moment) =>
  onChange && onChange(date ? date.utc().format(ISO_8601_WITHOUT_FRACTIONAL_SECONDS) : null)

export const handleOptionChange = (updater, datepickerRef) => event => {
  const picker = getPicker(datepickerRef)

  updater(mapOptionToSelected(event.target.value, picker ? picker.state.value : undefined))
}

export const handleTimeOptionClick = datepickerRef => () => {
  const picker = getPicker(datepickerRef)
  if (picker) {
    picker.input.click()
  }
}

interface OwnProps {
  value?: GetQuoteForm.Quote['shootStartAt']
  onChange?: (value: string | null) => void
  loading: boolean
  selectedLocation?: string
  selectedTime?: string
  title: React.ReactNode
  description: React.ReactNode
  color?: string | undefined
  iconNow?: string
  iconSchedule?: string
}

const EXCLUDE_ALERT_LOCATIONS_SLUGS = [
  'new_zealand',
  'singapore',
  'united_states',
  'australia',
  'united_kingdom',
  'japan',
]

export const PurchaseShootTime: React.SFC<OwnProps> = ({
  onChange,
  value,
  loading,
  selectedLocation,
  selectedTime,
  title,
  description,
  color,
  iconNow,
  iconSchedule,
}) => {
  const datepickerRef = React.createRef<React.Component>()

  const TOMORROW_DATE = moment(new Date()).add(1, 'day')

  const currentValue = mapSelectedToOption(value)

  return (
    <React.Fragment>
      <BoxHeader>
        <H5>{title}</H5>
        <H5SubText>{description}</H5SubText>
      </BoxHeader>

      {/* Only show the Alert if the selected location is NOT one of our main locations */}
      {/* and if the selected time is within the next 48 hours */}

      {selectedLocation &&
        !EXCLUDE_ALERT_LOCATIONS_SLUGS.includes(selectedLocation) &&
        (!selectedTime ||
          (new Date(selectedTime).getDate() === new Date().getDate() ||
            new Date(selectedTime).getDate() === new Date().getDate() + 1)) && (
          <StyledAlert
            message={null}
            description={
              <span>
                <strong>
                  <FormattedMessage id="purchase.shoot.time.alert.message" />
                </strong>{' '}
                <FormattedMessage id="purchase.shoot.time.alert.description" />
              </span>
            }
            type="info"
            showIcon
          />
        )}

      <Options value={currentValue} onChange={handleOptionChange(update(onChange), datepickerRef)}>
        <TimeOption disabled={loading} value="now" color={color}>
          {currentValue === 'now' && <SelectedIcon />}
          <OptionImage
            sizes={IMAGE_SIZES}
            source={{
              id: iconNow || 'time-profiles/icons/micro.svg',
              type: 'upload',
            }}
            rounded
          />
          <OptionType>
            <FormattedMessage id="purchase.shoot.time.now" />
          </OptionType>
          <OptionDesc>
            <FormattedMessage id="purchase.shoot.time.now_time" />
          </OptionDesc>
        </TimeOption>
        <TimeOption
          disabled={loading}
          value="schedule"
          onClick={handleTimeOptionClick(datepickerRef)}
          color={color}
        >
          {currentValue === 'schedule' && <SelectedIcon />}
          <OptionImage
            sizes={IMAGE_SIZES}
            source={{
              id: iconSchedule || 'time-profiles/icons/full.svg',
              type: 'upload',
            }}
            rounded
          />
          <OptionType>
            <FormattedMessage id="purchase.shoot.time.schedule" />
          </OptionType>
          <OptionDesc>
            <FormattedMessage id="purchase.shoot.time.pick_a_date" />
          </OptionDesc>
          <StyledDatePicker
            ref={datepickerRef}
            disabledDate={(date: moment.Moment) => date && date.isBefore(moment(), 'day')}
            format={DISPLAY_DATE_FORMAT}
            onChange={update(onChange)}
            defaultValue={value === null ? TOMORROW_DATE : moment(value)}
          />
        </TimeOption>
      </Options>
    </React.Fragment>
  )
}

export default PurchaseShootTime



// WEBPACK FOOTER //
// ./src/components/PurchaseShootTime/PurchaseShootTime.tsx